import React, { useState } from 'react';
import propTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  SparklesIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/solid';
import { Dialog } from '@headlessui/react';
import Button, { ButtonVariant } from './kit/Button';
import SelectContacts from './SelectContacts';
import Dropdown, { DropdownItem } from './kit/Dropdown';

/**
 * Pane to collect context for LLM-powered introduction generation
 */
export default function IntroContextPane({
  title,
  onGoBack,
  onGoForward,
  selectedContacts,
  templates = [],
  initialContext = '',
  initialTemplate = undefined,
}) {
  const { t } = useTranslation();
  const [contextText, setContextText] = useState(initialContext);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate);
  const [isGenerating, setIsGenerating] = useState(false);

  // LOGIC FIX:
  // We want to disable if context is TOO SHORT OR template is NOT SELECTED
  const isDisabled = contextText.trim().length < 15 || !selectedTemplate;

  const handleNext = async () => {
    if (isDisabled) return;

    setIsGenerating(true);
    try {
      await onGoForward(contextText, selectedTemplate);
    } catch (error) {
      console.error(error);
      setIsGenerating(false);
    }
  };

  return (
    <div className="sm:items-start">
      <div className="text-center sm:mt-0 sm:text-left">
        <Dialog.Title
          as="h3"
          className="text-base font-semibold text-gray-900 leading-6 border-b-2 border-gray-200 pb-3"
        >
          {title}
        </Dialog.Title>
        <div className="mt-4">
          <Button
            onClick={onGoBack}
            type="button"
            icon={<ArrowLeftIcon className="w-4 h-4 mr-2" />}
            variant={ButtonVariant.TEXT}
            disabled={isGenerating}
          >
            {t('intro_context_back_button')}
          </Button>
        </div>

        <div className="mt-4">
          <SelectContacts
            selectedContacts={{
              first: selectedContacts[0],
              second: selectedContacts[1],
            }}
            onContactRemove={() => {}}
            hideRemoveButtons
          />
        </div>
        <div className="mt-6">
          <Dropdown
            trigger={
              <Button variant={ButtonVariant.PRIMARY}>
                {selectedTemplate
                  ? selectedTemplate.name
                  : t('intro_modal_context_select_a_template_placeholder')}
                <ChevronDownIcon className="flex w-5 h-5" />
              </Button>
            }
          >
            <div className="max-h-60 overflow-y-auto">
              {templates.map((template) => (
                <DropdownItem key={template.id}>
                  <Button
                    variant={ButtonVariant.TEXT}
                    className="w-full justify-start hover:bg-gray-50 rounded-none h-10 px-4"
                    onClick={() => {
                      setSelectedTemplate(template);
                    }}
                  >
                    <span
                      className={
                        selectedTemplate?.id === template.id
                          ? 'font-bold text-primary'
                          : ''
                      }
                    >
                      {template.name}
                    </span>
                  </Button>
                </DropdownItem>
              ))}

              {templates.length === 0 && (
                <div className="p-4 text-sm text-gray-500 italic">
                  {t('intro_context_no_templates_found')}
                </div>
              )}
            </div>
          </Dropdown>
        </div>

        <div className="mt-6">
          <label
            htmlFor="intro-context"
            className="block text-sm font-medium text-gray-700"
          >
            {t('intro_context_label')}
          </label>
          <div className="mt-2">
            <textarea
              id="intro-context"
              name="intro-context"
              rows={6}
              disabled={isGenerating}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm p-3 border disabled:bg-gray-50"
              placeholder={t('intro_context_placeholder')}
              value={contextText}
              onChange={(e) => setContextText(e.target.value)}
            />
          </div>
          <div className="mt-2 flex justify-between">
            <p className="text-sm text-gray-500">
              {t('intro_context_description')}
            </p>
            <span
              className={`text-xs ${
                contextText.length >= 15 ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              {contextText.length}/15
            </span>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            disabled={isDisabled || isGenerating}
            type="button"
            variant={ButtonVariant.PRIMARY}
            onClick={handleNext}
            icon={
              isGenerating ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <SparklesIcon className="w-5 h-5 mr-2" />
              )
            }
          >
            {isGenerating
              ? t('intro_context_generating')
              : t('intro_context_submit_button')}
          </Button>
        </div>
      </div>
    </div>
  );
}

IntroContextPane.propTypes = {
  title: propTypes.string.isRequired,
  onGoBack: propTypes.func.isRequired,
  onGoForward: propTypes.func.isRequired,
  selectedContacts: propTypes.arrayOf(propTypes.shape({})).isRequired,
  templates: propTypes.arrayOf(propTypes.shape({})),
  initialContext: propTypes.string,
  initialTemplate: propTypes.shape({}),
};
